import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export type SetRow = {
  id: string;
  set_name: string;
  set_ref: string | null;
  set_image_url: string | null;
  set_theme: string;
  set_age_range: string;
  set_piece_count: number;
  set_description: string | null;
  set_price: number | null;
  set_weight: number | null;
  set_minifigs: number | null;
  set_status: string | null;
  catalogue_visibility: boolean;
  year_released: number | null;
  created_at: string;
  updated_at: string;
  set_full_description: string | null;
};

/**
 * Hook to fetch sets from the catalogue.
 * Only returns sets with catalogue_visibility = true by default.
 * @param limit - Maximum number of sets to fetch (default 50)
 * @param includeHidden - Whether to include hidden sets (default false)
 */
export const useSets = (limit: number = 50, includeHidden: boolean = false) => {
  return useQuery<SetRow[]>({
    queryKey: ["sets", limit, includeHidden],
    queryFn: async () => {
      let query = supabase
        .from("sets")
        .select("*")
        .order("set_name", { ascending: true })
        .limit(limit);

      if (!includeHidden) {
        query = query.eq("catalogue_visibility", true);
      }

      const { data, error } = await query;

      if (error) {
        console.error("Error fetching sets:", error);
        throw error;
      }

      return (data as SetRow[]) || [];
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

/**
 * Hook to fetch featured sets for the landing page.
 * Returns sets currently available in the user community pool, ordered by most recently added.
 * @param limit - Number of featured sets to show (default 8)
 */
export const useFeaturedSets = (limit: number = 8) => {
  return useQuery<SetRow[]>({
    queryKey: ["featured-sets", limit],
    queryFn: async () => {
      // Obtenemos los user_sets disponibles más recientes junto con sus sets asociados
      const { data, error } = await supabase
        .from("user_sets")
        .select(`
          created_at,
          sets!inner (
            id,
            set_name,
            set_ref,
            set_image_url,
            set_theme,
            set_age_range,
            set_piece_count,
            set_description,
            set_price,
            set_weight,
            set_minifigs,
            set_status,
            catalogue_visibility,
            year_released,
            created_at,
            updated_at,
            set_full_description
          )
        `)
        .eq("status", "available")
        .eq("sets.catalogue_visibility", true)
        .order("created_at", { ascending: false })
        .limit(Math.max(limit * 3, 24));

      if (error) {
        console.error("Error fetching featured sets:", error);
        throw error;
      }

      // Deduplicamos por ID de set para asegurar que cada set mostrado sea único
      const uniqueSets: SetRow[] = [];
      const seenSetIds = new Set<string>();

      for (const row of (data || []) as any[]) {
        const set = row.sets as SetRow | null;
        if (set && !seenSetIds.has(set.id)) {
          seenSetIds.add(set.id);
          uniqueSets.push(set);
          if (uniqueSets.length >= limit) {
            break;
          }
        }
      }

      return uniqueSets;
    },
    staleTime: 10 * 60 * 1000, // 10 minutes - featured sets change less frequently
  });
};

